// client/src/components/social/CommentThread.js
import React, { useState, useEffect } from 'react';
import {
  Box, Avatar, Typography, TextField, Button,
  IconButton, Tooltip, CircularProgress
} from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

function CommentItem({ comment, currentUserId, onDelete }) {
  const [liked, setLiked] = useState(
    comment.likes?.some(l => String(l.userId) === String(currentUserId)) || false
  );
  const [likeCount, setLikeCount] = useState(comment.likeCount || 0);
  const [disliked, setDisliked] = useState(
    comment.dislikes?.some(d => String(d.userId) === String(currentUserId)) || false
  );
  const [dislikeCount, setDislikeCount] = useState(comment.dislikeCount || 0);

  const user = comment.userId;
  const isOwner = String(user?._id) === String(currentUserId);

  const handleLike = async () => {
    try {
      if (liked) {
        await axiosInstance.delete(`/comments/${comment._id}/like`);
        setLiked(false); setLikeCount(c => c - 1);
      } else {
        await axiosInstance.post(`/comments/${comment._id}/like`);
        setLiked(true); setLikeCount(c => c + 1);
        if (disliked) { setDisliked(false); setDislikeCount(c => c - 1); }
      }
    } catch (err) { console.error('Comment like error:', err); }
  };

  const handleDislike = async () => {
    try {
      if (disliked) {
        await axiosInstance.delete(`/comments/${comment._id}/dislike`);
        setDisliked(false); setDislikeCount(c => c - 1);
      } else {
        const res = await axiosInstance.post(`/comments/${comment._id}/dislike`);
        setDisliked(true); setDislikeCount(c => c + 1);
        if (liked) { setLiked(false); setLikeCount(res.data.likeCount ?? likeCount - 1); }
      }
    } catch (err) { console.error('Comment dislike error:', err); }
  };
  const withinWindow = Date.now() - new Date(comment.createdAt).getTime() < 15 * 60 * 1000;
  const canDelete = isOwner && withinWindow;

  return (
    <Box sx={{ display: 'flex', gap: 1, py: 1 }}>
      <Avatar src={user?.profileImage} sx={{ width: 28, height: 28, flexShrink: 0 }} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography variant="caption" fontWeight={600}>{user?.username}</Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(comment.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 0.25 }}>{comment.body}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          {!isOwner && (
            <>
              <IconButton size="small" onClick={handleLike} sx={{ p: 0.25 }}>
                {liked ? <ThumbUpIcon fontSize="small" color="primary" /> : <ThumbUpOutlinedIcon fontSize="small" />}
              </IconButton>
              {likeCount > 0 && <Typography variant="caption">{likeCount}</Typography>}
              <IconButton size="small" onClick={handleDislike} sx={{ p: 0.25 }}>
                {disliked ? <ThumbDownIcon fontSize="small" color="error" /> : <ThumbDownOutlinedIcon fontSize="small" />}
              </IconButton>
              {dislikeCount > 0 && <Typography variant="caption" color="text.secondary">{dislikeCount}</Typography>}
            </>
          )}
          {canDelete && (
            <Tooltip title="Delete (within 15 min)">
              <IconButton size="small" onClick={() => onDelete(comment._id)} sx={{ p: 0.25 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Flat comment thread for reviews and photos.
 * Props: targetType ('review'|'photo'), targetId, currentUserId
 */
export default function CommentThread({ targetType, targetId, currentUserId }) {
  const { isFeatureEnabled } = useFeatureFlags();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isFeatureEnabled('enableComments') || !targetId) { setLoading(false); return; }
    axiosInstance.get(`/comments/${targetType}/${targetId}`)
      .then(res => {
        setComments(res.data.comments || []);
        setNextCursor(res.data.nextCursor || null);
        setHasMore(res.data.hasMore || false);
      })
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [targetType, targetId, isFeatureEnabled]);

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await axiosInstance.get(`/comments/${targetType}/${targetId}`, { params: { cursor: nextCursor } });
      setComments(prev => [...prev, ...(res.data.comments || [])]);
      setNextCursor(res.data.nextCursor || null);
      setHasMore(res.data.hasMore || false);
    } catch (err) {
      console.error('Error loading more comments:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (!isFeatureEnabled('enableComments')) return null;

  const handleSubmit = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      const res = await axiosInstance.post('/comments', { targetType, targetId, body: body.trim() });
      setComments(prev => [...prev, res.data]);
      setBody('');
    } catch (err) {
      console.error('Error posting comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axiosInstance.delete(`/comments/${commentId}`);
      setComments(prev => prev.filter(c => c._id !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  if (loading) return <CircularProgress size={16} />;

  return (
    <Box sx={{ mt: 1 }}>
      {comments.map(c => (
        <CommentItem key={c._id} comment={c} currentUserId={currentUserId} onDelete={handleDelete} />
      ))}
      {hasMore && (
        <Button size="small" onClick={handleLoadMore} disabled={loadingMore} sx={{ mt: 0.5 }}>
          {loadingMore ? 'Loading…' : 'Load more'}
        </Button>
      )}
      {currentUserId && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Add a comment…"
            value={body}
            onChange={e => setBody(e.target.value)}
            inputProps={{ maxLength: 500 }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
          />
          <Button variant="contained" size="small" onClick={handleSubmit} disabled={submitting || !body.trim()}>
            Post
          </Button>
        </Box>
      )}
    </Box>
  );
}
